import { twMerge } from 'tailwind-merge';
import type { EpcProjectOrgUnit } from '../../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { buildDownstreamNodes, buildUpstreamNodes, type DependencyNodeVm } from '../utils/dependencyNodes';
import { plannedTodayPercent, taskPercent } from '../utils/taskStatus';

const TONE_CARD: Record<DependencyNodeVm['tone'], string> = {
  done: 'border-l-[3px] border-l-[#047857]',
  run: 'border-l-[3px] border-l-[#b45309] bg-[#fffdf5]',
  idle: 'border-l-[3px] border-l-slate-300',
  blocked: 'border-l-[3px] border-l-[#be123c] bg-[#fff7f7]',
};
const TONE_TEXT: Record<DependencyNodeVm['tone'], string> = {
  done: 'text-[#047857]',
  run: 'text-[#b45309]',
  idle: 'text-slate-400',
  blocked: 'text-[#be123c]',
};

function DepNode({ node, onRetarget }: { node: DependencyNodeVm; onRetarget: (i: number) => void }) {
  const clickable = node.targetIndex != null;
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={() => clickable && onRetarget(node.targetIndex!)}
      className={twMerge(
        'relative w-full rounded-lg border border-[#e8edf1] bg-white px-2.5 py-1.5 text-left transition',
        TONE_CARD[node.tone],
        clickable ? 'hover:border-[#aecfbe] hover:bg-[#f6fbf8]' : 'cursor-default'
      )}
      title={clickable ? `Open ${node.label}` : undefined}
    >
      <span className="absolute -top-2 right-2 rounded border border-slate-200 bg-slate-100 px-1.5 text-[8px] font-bold text-slate-600">
        {node.linkBadge}
      </span>
      <span className="block truncate text-[10.5px] font-semibold text-slate-800">{node.label}</span>
      <span className="mt-0.5 block text-[9px] text-slate-400">{node.idLabel}</span>
      <span className={twMerge('mt-0.5 block text-[8.5px] font-bold', TONE_TEXT[node.tone])}>
        {node.statusLine}
        {node.deptName ? <span className="font-semibold"> · {node.deptName}</span> : null}
      </span>
    </button>
  );
}

export function TaskDependencyMap({
  tasks,
  index,
  orgUnits,
  today,
  onRetarget,
}: {
  tasks: EpcScheduleTaskRow[];
  index: number;
  orgUnits: EpcProjectOrgUnit[];
  today: Date;
  onRetarget: (i: number) => void;
}) {
  const task = tasks[index]!;
  const ups = buildUpstreamNodes(tasks, index, orgUnits);
  const downs = buildDownstreamNodes(tasks, index, orgUnits, today);
  const pct = Math.round(taskPercent(task));
  const planned = plannedTodayPercent(task, today);
  return (
    <div className="rounded-xl border border-[#e8edf1] bg-white px-3.5 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <p className="mb-2 text-[9.5px] font-bold uppercase tracking-[0.1em] text-slate-400">
        Dependency map · what blocks this ← task → what it blocks
      </p>
      <div className="grid grid-cols-[1fr_24px_1.1fr_24px_1fr] items-center">
        <div className="flex flex-col gap-1.5">
          {ups.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-200 px-2.5 py-3 text-center text-[10px] text-slate-400">
              No upstream links
            </p>
          ) : (
            ups.map((n, i) => <DepNode key={i} node={n} onRetarget={onRetarget} />)
          )}
        </div>
        <span className="text-center text-[13px] text-slate-400" aria-hidden>→</span>
        <div className="rounded-xl border-2 border-[#006346] bg-[#f6fbf8] px-2.5 py-2.5 text-center">
          <p className="truncate text-[11.5px] font-bold text-slate-900" title={String(task.name ?? '')}>
            {String(task.name ?? '').trim() || 'Task'}
          </p>
          <p className="mt-0.5 text-lg font-extrabold text-[#006346]">{pct}%</p>
          <p className="text-[9px] text-slate-500">
            {planned != null ? `planned ${planned}% by today` : 'no plan dates'}
          </p>
        </div>
        <span className="text-center text-[13px] text-slate-400" aria-hidden>→</span>
        <div className="flex flex-col gap-1.5">
          {downs.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-200 px-2.5 py-3 text-center text-[10px] text-slate-400">
              No downstream links
            </p>
          ) : (
            downs.map((n, i) => <DepNode key={i} node={n} onRetarget={onRetarget} />)
          )}
        </div>
      </div>
    </div>
  );
}
